import type { InputVector, TickInputs } from '@orbomb/sim';
import { normalizeInput } from '@orbomb/sim';
import { keysForCamp, localCamp } from '../settings';
import type { ControlPlan, KeySet } from '../settings';

const DEADZONE = 0.18;

function fromAxes(x: number, y: number): InputVector {
  const mag = Math.hypot(x, y);
  if (mag < DEADZONE) return { x: 0, y: 0 };
  const scaled = (mag - DEADZONE) / (1 - DEADZONE);
  return normalizeInput({ x: (x / mag) * scaled, y: (y / mag) * scaled });
}

function stronger(a: InputVector, b: InputVector): InputVector {
  return Math.hypot(a.x, a.y) >= Math.hypot(b.x, b.y) ? a : b;
}

function vectorFromKeys(binding: KeySet, keys: ReadonlySet<string>): InputVector {
  const held = (codes: readonly string[]) => codes.some((code) => keys.has(code));
  return normalizeInput({
    x: (held(binding.right) ? 1 : 0) - (held(binding.left) ? 1 : 0),
    y: (held(binding.down) ? 1 : 0) - (held(binding.up) ? 1 : 0),
  });
}

/**
 * Routes the keyboard, on-screen stick and gamepad through the active room plan.
 * A lone human inherits keys assigned to a bot camp, so arrow keys still move you
 * in person vs computer. Person vs person keeps the split from Settings.
 */
export function routeLocalInputs(
  keys: ReadonlySet<string>,
  stick: InputVector,
  gamepadLeft: InputVector,
  gamepadRight: InputVector,
  plan: ControlPlan,
): TickInputs {
  const assigned = keysForCamp(plan);
  const hands = localCamp(plan);
  const touch = plan.stickEnabled ? hands : null;
  const bothHuman = plan.turquoiseDriver === 'human' && plan.orangeDriver === 'human';
  const drive = (camp: 'TURQUOISE' | 'ORANGE', binding: KeySet): InputVector => {
    let vector = vectorFromKeys(binding, keys);
    if (touch === camp) vector = stronger(vector, stick);
    if (hands === camp) vector = stronger(vector, gamepadLeft);
    if (bothHuman && hands !== null && hands !== camp) vector = stronger(vector, gamepadRight);
    return vector;
  };
  return {
    ORANGE: drive('ORANGE', assigned.orange),
    TURQUOISE: drive('TURQUOISE', assigned.turquoise),
  };
}

/** Keyboard + analogue stick + optional gamepad. Physical key codes, not glyphs. */
export class Controls {
  private readonly keys = new Set<string>();
  private movementCodes = new Set<string>();
  private stick: InputVector = { x: 0, y: 0 };
  private pointerId: number | null = null;

  /** Keys chosen in Settings. Arrow keys and these codes do not scroll the page. */
  setMovementCodes(codes: ReadonlySet<string>): void {
    this.movementCodes = new Set(codes);
  }

  constructor(
    private readonly stickEl: HTMLElement,
    private readonly knobEl: HTMLElement,
  ) {
    const onKeyDown = (e: KeyboardEvent) => {
      this.keys.add(e.code);
      const menu = document.getElementById('home');
      const settings = document.getElementById('settings');
      const auth = document.getElementById('auth');
      const lobby = document.getElementById('lobby');
      const multi = document.getElementById('multi');
      const menuOpen = Boolean(
        (menu && !menu.hidden) ||
        (settings && !settings.hidden) ||
        (auth && !auth.hidden) ||
        (lobby && !lobby.hidden) ||
        (multi && !multi.hidden),
      );
      if (!menuOpen && (this.movementCodes.has(e.code) || e.code === 'Space')) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', (e) => this.keys.delete(e.code), true);
    window.addEventListener('blur', () => {
      this.keys.clear();
      this.resetStick();
    });

    const onDown = (e: PointerEvent) => {
      this.pointerId = e.pointerId;
      this.stickEl.setPointerCapture(e.pointerId);
      this.applyPointer(e);
    };
    const onMove = (e: PointerEvent) => {
      if (this.pointerId !== e.pointerId) return;
      this.applyPointer(e);
    };
    const onUp = (e: PointerEvent) => {
      if (this.pointerId !== e.pointerId) return;
      this.pointerId = null;
      this.resetStick();
    };
    this.stickEl.addEventListener('pointerdown', onDown);
    this.stickEl.addEventListener('pointermove', onMove);
    this.stickEl.addEventListener('pointerup', onUp);
    this.stickEl.addEventListener('pointercancel', onUp);
  }

  read(plan: ControlPlan): TickInputs {
    return routeLocalInputs(this.keys, this.stick, this.gamepad(0), this.gamepad(2), plan);
  }

  private gamepad(axisOffset: number): InputVector {
    const pads = typeof navigator !== 'undefined' ? navigator.getGamepads() : [];
    const pad = pads[0];
    if (!pad) return { x: 0, y: 0 };
    return fromAxes(pad.axes[axisOffset] ?? 0, pad.axes[axisOffset + 1] ?? 0);
  }

  private applyPointer(e: PointerEvent): void {
    const rect = this.stickEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const nx = (e.clientX - cx) / (rect.width / 2);
    const ny = (e.clientY - cy) / (rect.height / 2);
    this.stick = fromAxes(nx, ny);
    const px = this.stick.x * 28;
    const py = this.stick.y * 28;
    this.knobEl.style.transform = `translate(${px}px, ${py}px)`;
  }

  private resetStick(): void {
    this.stick = { x: 0, y: 0 };
    this.knobEl.style.transform = 'translate(0, 0)';
  }
}
